import type { Unit } from 'dxf-writer'
import {
  convertLengthFromMeters,
  type DisplayUnit,
  type FeetFormat,
  normalizeUnitPreference,
  type UnitPreference,
} from '@/shared/unitPresentation'

/** 基于米制 CAD 源数据的 DXF 导出配置。 */
export interface DxfExporterOptions {
  /**
   * DXF 的目标展示单位。
   *
   * 英尺—英寸格式遵循 DXF Architectural 约定，内部模型空间使用英寸。
   */
  displayUnit?: DisplayUnit
  /** 目标展示单位为英尺时的全局长度与标注格式。 */
  feetFormat?: FeetFormat
}

/** DXF 导出期间统一使用的单位与缩放上下文。 */
export interface DxfExportContext {
  /** 标注展示偏好。 */
  unitPreference: UnitPreference
  /** 实际 DXF 坐标单位，同时写入 `$INSUNITS`。 */
  dxfUnit: Unit
  /** 米制模型坐标到实际 DXF 坐标单位的倍率。 */
  modelScale: number
  /** 毫米图框规范到实际 DXF 坐标单位的倍率。 */
  millimeterScale: number
  /** 写入 `$LUNITS` 的全局长度显示类型；旧位置参数路径不设置。 */
  lengthDisplayFormat?: number
  /** 写入 `$LUPREC` 的全局长度显示精度；旧位置参数路径不设置。 */
  lengthDisplayPrecision?: number
  /** 是否按新接口的米制源数据规则格式化标注。 */
  formatsMeterSource: boolean
}

const LENGTH_DISPLAY_PRECISIONS: Record<DisplayUnit, number> = {
  m: 3,
  cm: 1,
  mm: 0,
  in: 2,
  ft: 2,
}

const DISPLAY_UNIT_TO_DXF_UNIT: Record<DisplayUnit, Unit> = {
  m: 'Meters',
  cm: 'Centimeters',
  mm: 'Millimeters',
  in: 'Inches',
  ft: 'Feet',
}

const DXF_UNIT_TO_DISPLAY_UNIT: Partial<Record<Unit, DisplayUnit>> = {
  Meters: 'm',
  Centimeters: 'cm',
  Millimeters: 'mm',
  Inches: 'in',
  Feet: 'ft',
}

/** 为配置对象路径创建基于米制源数据的导出上下文。 */
export function createDxfExportContext(
  options: DxfExporterOptions | undefined,
  fallbackUnit: DisplayUnit
): DxfExportContext {
  const unitPreference = normalizeUnitPreference({
    displayUnit: options?.displayUnit ?? fallbackUnit,
    feetFormat: options?.feetFormat,
  })
  const usesArchitecturalCoordinates =
    unitPreference.displayUnit === 'ft' && unitPreference.feetFormat === 'feet-inches'
  const coordinateUnit = usesArchitecturalCoordinates ? 'in' : unitPreference.displayUnit

  return {
    unitPreference,
    dxfUnit: DISPLAY_UNIT_TO_DXF_UNIT[coordinateUnit],
    modelScale: convertLengthFromMeters(1, coordinateUnit),
    millimeterScale: convertLengthFromMeters(0.001, coordinateUnit),
    lengthDisplayFormat: usesArchitecturalCoordinates ? 4 : 2,
    lengthDisplayPrecision: usesArchitecturalCoordinates
      ? 4
      : LENGTH_DISPLAY_PRECISIONS[unitPreference.displayUnit],
    formatsMeterSource: true,
  }
}

/** 为旧位置参数路径创建兼容上下文。 */
export function createLegacyDxfExportContext(
  dxfUnit: Unit,
  modelScale: number,
  millimeterScale: number = 1
): DxfExportContext {
  const displayUnit = DXF_UNIT_TO_DISPLAY_UNIT[dxfUnit] ?? 'm'
  return {
    unitPreference: normalizeUnitPreference({ displayUnit, feetFormat: 'decimal' }),
    dxfUnit,
    modelScale,
    millimeterScale,
    formatsMeterSource: false,
  }
}

/** 判断参数是否为新的 DXF 导出配置对象。 */
export function isDxfExporterOptions(
  value: DxfExporterOptions | Unit | undefined
): value is DxfExporterOptions {
  return typeof value === 'object' && value !== null
}
